import { RJSFValidationError, UiSchema } from '@rjsf/utils'
import groupBy from 'lodash/groupBy'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'

import { IFormDataToolForm } from './components/material-forms/models/IFormDataToolForm'
import {
    cornerDrawablesSortOrder,
    DRAWABLE_GROUPPING_NAMES,
    eaveGableDrawablesSortOrder,
    exteriorRoofingDrawablesSortOrder,
    floorTrussDrawablesSortOrder,
    roofingBeamDrawablesSortOrder,
    wallsDrawablesSortOrder,
} from './constants/drawables-sort-order'
import { GeometryGroup, GeometryGroupWithTempType } from '../../../imup/slices/geometry'
import { LocationType } from '../../../imup/types'
import { ActiveDrawable } from '../../../models/activeDrawable'
import { GeneralDrawableSettings } from '../../../models/activeDrawableSettings'
import { AIAutomatedObject, AIMaterial, SortedSuggestion } from '../../../models/aiClassifications'
import { Project } from '../../../models/project'
import { DrawableGroupsCategorizedByBundleAndSorted } from '../../../shared/components/navigation-tree/utils/types'
import { BEAM_BUNDLE_NAME_BY_LOCATION } from '../../../shared/constants/beam-types'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { FORM_ERROR_MESSAGES, TRANSFORM_ERRORS } from '../../../shared/constants/error-messages'
import IndexableObject from '../../../shared/constants/general-enums/indexableObject'
import { PROJECT_TYPES_ENUM } from '../../../shared/constants/project-type.constants'
import { PROJECT_STATUS_NAME } from '../../../shared/constants/projectStatusName'
import { addTempTypeToDrawables } from '../../../shared/services/drawable-groups-service'
import { convertFullNumberFractionToDecimal } from '../../../utils/formSchema/numberConversion'
import { sortASCByNumberOrText } from '../../../utils/sorting'
import { capitalizeEachWord, convertStringToBool, prepareDrawableGroupTempType } from '../../../utils/stringFormatters'

enum RJSF_ERROR_NAMES {
    IF = 'if',
    REQUIRED = 'required',
}

const locationModuleNaming = [
    DRAWABLE_TYPES.BEAM.toString(),
    DRAWABLE_TYPES.FLOOR_SYSTEMS.toString(),
    DRAWABLE_TYPES.ROOF_SYSTEMS.toString(),
    DRAWABLE_TYPES.RIMBOARD.toString(),
]

const parseBeamDimension = (value: string | null | undefined): number => {
    if (!value) return 0

    // Plain integers and decimals don't need fraction conversion
    if (!value.includes('/')) return parseFloat(value) || 0

    return parseFloat(convertFullNumberFractionToDecimal(value) ?? '0') || 0
}

const handleSortDrawableGroupsDrawables = (
    drawableGroupType: DRAWABLE_GROUPPING_NAMES | DRAWABLE_TYPES,
    drawableGroupDrawables: GeometryGroup[]
): GeometryGroupWithTempType[] => {
    // sort by specific order
    if (drawableGroupType === DRAWABLE_GROUPPING_NAMES.WALL) {
        return drawableGroupDrawables
            .map((drawable) => {
                return {
                    ...drawable,
                    order: wallsDrawablesSortOrder.indexOf(drawable.settings.floor_level?.toLowerCase() ?? '') + 1,
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    if (drawableGroupType === DRAWABLE_TYPES.CORNER) {
        return drawableGroupDrawables
            .map((drawable) => {
                return {
                    ...drawable,
                    order: cornerDrawablesSortOrder.indexOf(`${drawable.settings.location?.toLowerCase()}`.trim()) + 1,
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    if (drawableGroupType === DRAWABLE_GROUPPING_NAMES.FLOOR_TRUSS) {
        return drawableGroupDrawables
            .map((drawable) => {
                return {
                    ...drawable,
                    order: floorTrussDrawablesSortOrder.indexOf(`${drawable.settings.type?.toLowerCase()}`.trim()) + 1,
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    if (drawableGroupType === DRAWABLE_GROUPPING_NAMES.EAVE_GABLE) {
        return drawableGroupDrawables
            .map((drawable) => {
                return {
                    ...drawable,
                    order:
                        eaveGableDrawablesSortOrder.indexOf(
                            `${drawable.settings.name
                                ?.replaceAll(drawable.settings.selection ?? '', '')
                                ?.toLowerCase()}`.trim()
                        ) + 1,
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    if (drawableGroupType === DRAWABLE_GROUPPING_NAMES.ROOFING_BEAM) {
        return drawableGroupDrawables
            .map((drawable) => {
                return {
                    ...drawable,
                    order: roofingBeamDrawablesSortOrder.indexOf(`${drawable.type}`.trim()) + 1,
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    if (drawableGroupType === DRAWABLE_GROUPPING_NAMES.EXTERIOR_ROOFING) {
        return drawableGroupDrawables
            .map((drawable) => {
                const order = exteriorRoofingDrawablesSortOrder.findIndex((name) =>
                    drawable.settings.name
                        ?.replace(drawable.settings?.selection ?? '', '')
                        ?.toLowerCase()
                        .includes(name)
                )

                return {
                    ...drawable,
                    order: order === -1 ? exteriorRoofingDrawablesSortOrder.length + 1 : order,
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    if (drawableGroupType === DRAWABLE_TYPES.BEAM || drawableGroupDrawables[0]?.settings?.beam_type) {
        // Multiplier ensures thickness is the primary sort key and width is secondary (width values must be < 1000)
        const thicknessSortWeight = 1000

        return drawableGroupDrawables
            .map((drawable) => {
                const dims = drawable.settings.ewp_beam_dimensions ?? drawable.settings.dimensional_beam_dimensions

                return {
                    ...drawable,
                    order: parseBeamDimension(dims?.thickness) * thicknessSortWeight + parseBeamDimension(dims?.width),
                }
            })
            .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
    }

    // general sorting by drawable name
    return drawableGroupDrawables.sort((a, b) => sortASCByNumberOrText(a.settings.name ?? '', b.settings.name ?? ''))
}

export const sortedDrawableGroups = (openingGroups: GeometryGroup[]) => {
    // added tempType to group them and do not change a current type
    const drawablesWithTempType = addTempTypeToDrawables(openingGroups)

    const groupedDrawables = groupBy(drawablesWithTempType, (group) => {
        const {
            bundle_name,
            bundle_location,
            bundle_floor_level,
            bundle_other_floor_level,
            floor_level,
            location,
            secondary_location,
        } = group.settings

        const floorLevel = bundle_other_floor_level || bundle_floor_level || floor_level

        if (locationModuleNaming.includes(group.type?.toLowerCase())) {
            if (BEAM_BUNDLE_NAME_BY_LOCATION.includes(group.settings.location as LocationType)) {
                return location?.trim().toLowerCase() || ''
            }

            // removes "floor" from the floor level string if the location is "floor deck"
            // so that bundle name is not "2nd Floor Floor Deck"
            const updatedFloorLevel =
                group.settings.location === LocationType.FLOOR_DECK
                    ? floorLevel
                          ?.toLowerCase()
                          .replace(/\s*floor/i, '')
                          .trim()
                    : floorLevel?.toLowerCase()

            const nameSegments = [updatedFloorLevel, secondary_location, location].filter((segment) =>
                Boolean(segment?.trim())
            )

            if (nameSegments.length > 0) {
                return nameSegments.join(' ').toLowerCase()
            }

            // This will only be reached with temporary Beam
            return ''
        }
        if (bundle_name !== 'FRAMING' && bundle_name !== 'EXTERIOR') {
            const segments = [
                floorLevel?.toLowerCase() || '',
                bundle_location?.toLowerCase() || '',
                bundle_name?.toLowerCase(),
            ]
            const uniqueSegments = [...new Set(segments.filter(Boolean))]

            return uniqueSegments.join(' ').trim().toLowerCase()
        }

        return (group.tempType ?? prepareDrawableGroupTempType(group.settings.name || '') ?? '').toLowerCase()
    })

    return Object.entries(groupedDrawables)
        .map((drawableGroup) => {
            const drawableGroupType = drawableGroup[0]
            const drawableGroupDrawables = drawableGroup[1]
            const drawableGroupTypeInsideABundle = drawableGroup[1][0]?.type

            return {
                type: drawableGroupType,
                order: Object.keys(DRAWABLE_GROUPPING_NAMES).indexOf(drawableGroupType.toUpperCase()) + 1,
                openingGroups: handleSortDrawableGroupsDrawables(
                    DRAWABLE_GROUPPING_NAMES[drawableGroupType.toUpperCase()] ||
                        DRAWABLE_TYPES[drawableGroupType.toUpperCase()] ||
                        DRAWABLE_TYPES[drawableGroupTypeInsideABundle.toUpperCase()],
                    drawableGroupDrawables
                ),
            }
        })
        .sort((a, b) => sortASCByNumberOrText(a.order, b.order))
}

export const sortDrawableGroupsByBundleNameOrType = (
    openingGroups: GeometryGroup[]
): DrawableGroupsCategorizedByBundleAndSorted[] => {
    return Object.entries(groupBy(openingGroups, 'settings.folder')).map(([bundleTitle, groupsInBundle]) => ({
        title: bundleTitle,
        sortedGroupsInBundle: sortedDrawableGroups(
            groupsInBundle.map((group) => {
                if (
                    group.type === DRAWABLE_TYPES.BEAM &&
                    group.settings.beam_type &&
                    Object.values(DRAWABLE_TYPES).includes(group.settings.beam_type as DRAWABLE_TYPES)
                ) {
                    group.type = group.settings.beam_type as DRAWABLE_TYPES
                }

                return group
            })
        ),
    }))
}

export const sortAIByTypeAndSettings = (aiSuggestions: AIAutomatedObject[]): SortedSuggestion[] => {
    const groupedMaterials = aiSuggestions.reduce((acc, suggestion) => {
        const { type, settings } = suggestion
        const { location, size } = settings
        const subType: DRAWABLE_TYPES | null = settings.beam_type ?? null
        const floorLevel = type === DRAWABLE_TYPES.WALL ? settings.floor_level : ''

        const settingsName = capitalizeEachWord(
            `${settings.type ? settings.type : prepareDrawableGroupTempType(type)}${location ? ` - ${location}` : ''}${
                floorLevel ? ` - ${floorLevel}` : ''
            }${size ? ` - ${size}` : ''}`
        )

        const groupingType = subType || type
        const typeName = capitalizeEachWord(prepareDrawableGroupTempType(groupingType))
        const typeGroup = acc.find((group) => group.type === groupingType || group.subType === groupingType)

        if (!typeGroup) {
            acc.push({
                type,
                subType,
                name: typeName,
                settingsGroups: [
                    {
                        type,
                        name: settingsName,
                        aiMaterials: [suggestion],
                    },
                ],
            })
        } else {
            const settingsGroup = typeGroup.settingsGroups.find((group) => group.name === settingsName)

            if (!settingsGroup) {
                typeGroup.settingsGroups.push({
                    type,
                    name: settingsName,
                    aiMaterials: [suggestion],
                })
            } else {
                settingsGroup.aiMaterials.push(suggestion)
            }
        }

        return acc
    }, [] as { type: DRAWABLE_TYPES; settingsGroups: AIMaterial[]; subType: DRAWABLE_TYPES | null; name: string }[])

    return setVisibilityofAIGroups(groupedMaterials)
}

/** Method returns true, if project type is Markup or Markup Only */
export const isMarkupProject = (project: Project): boolean => {
    return (
        project.type === PROJECT_TYPES_ENUM.INTERACTIVE_MARKUP ||
        project.type === PROJECT_TYPES_ENUM.INTERACTIVE_MARKUP_ONLY
    )
}

export const doesProjectNeedDigitization = (project: Project): boolean => {
    const isMarkup = isMarkupProject(project)
    const isProjectStatusNeedDigitization =
        project?.projectStatus?.isProjectEditable ||
        project?.projectStatus?.name === PROJECT_STATUS_NAME.ERROR_PENDING_COMPLETION

    return isMarkup && isProjectStatusNeedDigitization
}

export const removeNullFieldsDeep = (obj: IndexableObject) => {
    if (typeof obj !== 'object' || obj === null) {
        return obj
    }

    // recursively process each key-value pair
    const result = Object.entries(obj).reduce((acc, [key, value]) => {
        if (typeof value === 'object' && value !== null) {
            const cleanedValue = removeNullFieldsDeep(value)

            if (Object.keys(cleanedValue).length > 0) {
                acc[key] = cleanedValue
            }
        } else if (value !== null && value !== '') {
            acc[key] = value
        }

        return acc
    }, {})

    return result
}

const NUMERIC_SETTING_KEYS = new Set(['source_measurement_id', 'version'])

export const prepareActiveDrawableSettings = (
    uiSchema: UiSchema,
    settings: ActiveDrawable['settings']
): ActiveDrawable['settings'] =>
    Object.keys(settings).reduce((newSettings, key) => {
        if (uiSchema?.[key]?.['ui:field'] === 'SwitchField') {
            newSettings[key] = convertStringToBool(settings[key])
        } else if (
            isNull(settings[key]) ||
            (typeof settings[key] !== 'number' && typeof settings[key] !== 'boolean' && isEmpty(settings[key]))
        ) {
            // if there is no value, just remove the field
            delete newSettings[key]
        } else if (isArray(settings[key]) || typeof settings[key] === 'boolean') {
            // keep arrays and booleans as it is, do not convert them into string
            newSettings[key] = settings[key]
        } else if (NUMERIC_SETTING_KEYS.has(key)) {
            // keep ID fields as numbers, do not convert them into strings
            newSettings[key] = Number(settings[key])
        } else if (!isUndefined(settings[key]) && typeof settings[key] !== 'object') {
            // All values supplied by selections API are strings and accepted by gateway/material calc.
            // "Other" free text input fields should be strings as well
            newSettings[key] = String(settings[key])
        } else if (typeof settings[key] === 'object' && !isUndefined(settings[key])) {
            newSettings[key] = removeNullFieldsDeep(settings[key])
        }

        return newSettings
    }, {})

/**
 * Receive distance and convert to feet and inches
 *
 * @param distance
 */
export const convertAbsoluteDistanceToFeetAndInches = (distance: number): { feet: number; inches: number } => {
    const feet = Math.floor(distance)
    const inches = Math.round((distance - feet) * 12)

    // in the case of 12 inches, convert, add 1 to feet and display 0 in inches
    // 4 feet 12 inches should be 5 feet 0 inches
    const preparedFeet = inches === 12 ? feet + 1 : feet
    const preparedInches = inches === 12 ? 0 : inches

    return {
        feet: preparedFeet,
        inches: preparedInches,
    }
}

/**
 * get feet and inches and format as string
 *
 * @param distance
 */
export const convertAbsoluteDistanceToFormattedString = (distance: number): string => {
    const { feet, inches } = convertAbsoluteDistanceToFeetAndInches(distance)

    return `${feet}' ${inches}"`
}

export const transformErrors = (
    errors: RJSFValidationError[],
    formData: GeneralDrawableSettings | null | IFormDataToolForm
) => {
    const errorList: RJSFValidationError[] = []

    // prevent display error message if field expect to get value but value doesn't exist
    errors.forEach((error: RJSFValidationError) => {
        // In the case of a schema error
        // such as must match "then" schema
        // do not show the error as it is not useful
        // to estimators see: https://portal.myparadigm.com/browse/BLUEPRINTS-24005
        if (error.name === RJSF_ERROR_NAMES.IF && error.message && error.message.includes('schema')) {
            return
        }

        if (error.name === RJSF_ERROR_NAMES.REQUIRED) {
            error.message = FORM_ERROR_MESSAGES.REQUIRED
        }

        // get all the transform errors keys and values and find in list of errors
        const errorPatternFound = Object.entries(TRANSFORM_ERRORS).find(
            ([, pattern]) => pattern === error.params?.pattern
        )

        // if there is pattern in schema and we defined error message set it
        if (!!errorPatternFound?.[0] && FORM_ERROR_MESSAGES?.[errorPatternFound[0]]) {
            error.message = FORM_ERROR_MESSAGES[errorPatternFound[0]]
        }

        // we would like to allow null values, even if it's not present in options
        // if null is not allowed, isRequired error will do the validation
        // property format ex is '.location', remove dot to find the value
        if (error.message !== FORM_ERROR_MESSAGES.EMPTY_CASE && formData) {
            error.property?.replace(/^./, '')
            errorList.push(error)
        }
    })

    return errorList
}

/**
 * Parse `8'6"` to { feet: 8, inches: 6 }
 * Parse `8'3/4"` to { feet: 8, inches: 3/4 }
 * Parse `8'1-3/4"` to { feet: 8, inches: 1-3/4 }
 * @param feetAndInches
 */
export const parseFeetAndInches = (feetAndInches: string): { feet: string; inches: string } | undefined => {
    const pattern = /(\d+)'((\d+-)?\d+(\/\d+)?)"/
    const match = feetAndInches.match(pattern)

    if (match) {
        const feet = match[1]
        const inches = match[2]

        return { feet, inches }
    }

    return undefined
}

/**
 * Based on feet and inches object, return the string
 * @param feetAndInches
 */
export const generateFeetAndInchesString = (feetAndInches: { feet: string; inches: string }): string => {
    const feet = feetAndInches.feet || '0'
    const inches = feetAndInches.inches || '0'

    return `${feet}'${inches}"`
}

/**
 * Set visibility of AI groups
 * @param sortedAISuggestions
 */
export const setVisibilityofAIGroups = (sortedAISuggestions: SortedSuggestion[]): SortedSuggestion[] => {
    for (const group of sortedAISuggestions) {
        for (const settingsGroup of group.settingsGroups) {
            settingsGroup.isVisible = settingsGroup.aiMaterials.some((item) => item.isVisible !== false)
        }
        group.isVisible = group.settingsGroups.some((settingsGroup) => settingsGroup.isVisible)
    }

    return sortedAISuggestions
}

/**
 * Determine if there is any content in the text editor
 * @param editorValue
 */
export const isEditorEmpty = (editorValue: string): boolean => {
    const emptyContent = ['', '<p><br></p>']

    return emptyContent.includes(editorValue.trim())
}

/**
 * Input date in format '2023-10-01T12:00:00Z' and return formatted date string
 * Example: 'October 1 at 12:00 PM'
 * @param date
 */
export const getFlagTooltipDate = (date: string | null): string => {
    if (!date) return ''

    const formattedDate = date.replace(/Z$/, '').replace('T', ' ')

    const parsedDate = new Date(`${formattedDate} UTC`)

    if (isNaN(parsedDate.getTime())) return ''

    return parsedDate.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

/**
 * Input date in format '2023-10-01T12:00:00' and return formatted date string
 * Example: '6/30/2025 at 12:39 PM'
 * @param date
 */
export const getFormattedFlagCreationDate = (date: string | null): string => {
    if (!date) return ''
    const parsedDate = new Date(`${date} UTC`)

    if (isNaN(parsedDate.getTime())) return ''

    const datePart = parsedDate.toLocaleDateString('en-US')
    const timePart = parsedDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

    return `${datePart} at ${timePart}`
}

const CEILING_JOIST_TYPE = 'Ceiling Joist'
const RAFTER_TYPE = 'Rafter'

const FRAMING_TYPE_TO_ADVANCED_SETTINGS_KEY: Record<string, 'joist' | 'rafters'> = {
    [CEILING_JOIST_TYPE]: 'joist',
    [RAFTER_TYPE]: 'rafters',
}

const GL_TYPE_SYNC_DRAWABLE_TYPES = [DRAWABLE_TYPES.ROOF_SYSTEMS, DRAWABLE_TYPES.FLOOR_SYSTEMS]

// Sections may hold undefined values (e.g. left by a previous sync), so only defined values are merged
const definedValues = (section?: Record<string, any>) => omitBy(section, isUndefined)

/**
 * Roof systems keep advanced settings under `joist` (Ceiling Joist) or `rafters` (Rafter),
 * and only the section of the current type exists in the schema.
 * When the framing type changes, carry the values of the previous type's section over to the new one.
 * Values already set in the new section are kept.
 */
const carryOverAdvancedSettingsOnFramingTypeChange = (
    updatedFormData: GeneralDrawableSettings,
    previousFormData?: GeneralDrawableSettings | null
): GeneralDrawableSettings => {
    const previousKey = FRAMING_TYPE_TO_ADVANCED_SETTINGS_KEY[String(previousFormData?.type)]
    const newKey = FRAMING_TYPE_TO_ADVANCED_SETTINGS_KEY[String(updatedFormData.type)]

    if (!previousKey || !newKey || previousKey === newKey) return updatedFormData

    const source = {
        ...definedValues(previousFormData?.advanced_settings?.[previousKey]),
        ...definedValues(updatedFormData.advanced_settings?.[previousKey]),
    }

    if (isEmpty(source)) return updatedFormData

    return {
        ...updatedFormData,
        advanced_settings: {
            ...updatedFormData.advanced_settings,
            [newKey]: { ...source, ...definedValues(updatedFormData.advanced_settings?.[newKey]) },
        },
    }
}

/**
 * Returns the gl_type that both joist and rafters should have, or null when nothing should be synced.
 * On a framing type change the schema may have dropped the old section, so the first defined value wins
 * instead of treating the missing one as a change.
 */
const getTargetGlType = (
    updatedFormData: GeneralDrawableSettings,
    previousFormData?: GeneralDrawableSettings | null
): string | undefined | null => {
    const newJoistGlType = updatedFormData.advanced_settings?.joist?.gl_type
    const newRaftersGlType = updatedFormData.advanced_settings?.rafters?.gl_type
    const prevJoistGlType = previousFormData?.advanced_settings?.joist?.gl_type
    const prevRaftersGlType = previousFormData?.advanced_settings?.rafters?.gl_type

    if (updatedFormData.type !== previousFormData?.type) {
        return newJoistGlType ?? newRaftersGlType ?? prevJoistGlType ?? prevRaftersGlType ?? null
    }

    if (newJoistGlType !== prevJoistGlType) return newJoistGlType
    if (newRaftersGlType !== prevRaftersGlType) return newRaftersGlType

    return null
}

const syncGlType = (
    updatedFormData: GeneralDrawableSettings,
    previousFormData?: GeneralDrawableSettings | null
): GeneralDrawableSettings => {
    if (!updatedFormData.advanced_settings) return updatedFormData

    const targetGlType = getTargetGlType(updatedFormData, previousFormData)

    if (targetGlType === null) return updatedFormData

    const { joist, rafters } = updatedFormData.advanced_settings

    return {
        ...updatedFormData,
        advanced_settings: {
            ...updatedFormData.advanced_settings,
            joist: { ...joist, gl_type: targetGlType },
            rafters: { ...rafters, gl_type: targetGlType },
        },
    }
}

/**
 * Keeps joist/rafters advanced settings of roof/floor systems in sync on form change:
 * - roof systems: carries the previous type's section over to the new one when the framing type changes
 * - roof/floor systems (multiple): mirrors gl_type into both sections
 */
export const syncJoistAndRaftersAdvancedSettings = (
    updatedFormData: GeneralDrawableSettings,
    previousFormData?: GeneralDrawableSettings | null,
    drawableType?: DRAWABLE_TYPES
): GeneralDrawableSettings => {
    if (!drawableType) return updatedFormData

    let result = updatedFormData

    if (drawableType === DRAWABLE_TYPES.ROOF_SYSTEMS) {
        result = carryOverAdvancedSettingsOnFramingTypeChange(result, previousFormData)
    }

    if (GL_TYPE_SYNC_DRAWABLE_TYPES.includes(drawableType)) {
        result = syncGlType(result, previousFormData)
    }

    return result
}
